import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Flex, Menu, MenuButton, MenuItem, MenuList, Portal } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import type { ResponsiveValue } from '@chakra-ui/react';
import { useIsMobile } from '../../hooks/use-is-mobile';
import { Text } from '../../ui-components';
import { CheckIcon, ChevronDownBigIcon, ChevronUpBigIcon } from '../icons';
import type { SLTMenuVariant } from '../../theme/components/base/menu';
import type { DropdownItem, DropdownItems } from './DropdownItem';

interface Props {
    id: string;
    items: DropdownItems;
    currentItem?: DropdownItem;
    buttonPrefix?: string;
    onChange: (value: string) => void;
    variant?: SLTMenuVariant | ResponsiveValue<SLTMenuVariant>;
}

export const Dropdown = ({
    id,
    items,
    currentItem,
    buttonPrefix,
    onChange,
    variant = 'slt-dropdown-outline',
}: Props): ReactElement | null => {
    const [selectedItem, setSelectedItem] = useState(currentItem);

    const onClickItem = useCallback(
        (item: DropdownItem): void => {
            setSelectedItem(item);
            onChange(item.value);
        },
        [onChange],
    );

    const isMobile = useIsMobile();

    useEffect(() => {
        setSelectedItem(currentItem);
    }, [currentItem]);

    if (!items.length) {
        return null;
    }

    return (
        <Menu variant={variant}>
            {({ isOpen }) => (
                <>
                    <MenuButton data-testid={`${id}-dropdown-menu-button`}>
                        <Flex
                            data-testid={`${id}-dropdown-menu-button-content`}
                            alignItems="center"
                            justifyContent="space-between"
                            gap={1}
                        >
                            {selectedItem?.label ? (
                                <>
                                    {buttonPrefix && (
                                        <Text
                                            id={`${id}-prefix-label`}
                                            style="bold"
                                            size={isMobile ? 'small' : 'base'}
                                            trim
                                        >
                                            {buttonPrefix}
                                        </Text>
                                    )}

                                    {selectedItem.label}
                                </>
                            ) : (
                                <>
                                    <FormattedMessage id="select" defaultMessage="Select" />
                                    ...
                                </>
                            )}

                            <Flex justifyContent="center" width={4}>
                                {isOpen ? <ChevronUpBigIcon /> : <ChevronDownBigIcon />}
                            </Flex>
                        </Flex>
                    </MenuButton>

                    <Portal>
                        <MenuList
                            zIndex={150}
                            defaultValue={selectedItem?.value}
                            data-testid={`${id}-dropdown-list`}
                        >
                            {items.map((item) => {
                                const isSelected = selectedItem?.id === item.id;

                                return (
                                    <MenuItem
                                        data-testid={`${id}-dropdown-item-${item.id}`}
                                        key={item.id}
                                        value={item.value}
                                        onClick={() => onClickItem(item)}
                                        style={
                                            isSelected
                                                ? {
                                                      backgroundColor: '#519DFF',
                                                      borderRadius: '4px',
                                                      color: '#fff',
                                                  }
                                                : undefined
                                        }
                                    >
                                        <Flex
                                            alignItems="center"
                                            justifyContent="center"
                                            width={6}
                                            height={6}
                                        >
                                            {isSelected && <CheckIcon />}
                                        </Flex>

                                        {item.label}
                                    </MenuItem>
                                );
                            })}
                        </MenuList>
                    </Portal>
                </>
            )}
        </Menu>
    );
};

Dropdown.defaultProps = {
    variant: 'slt-dropdown-outline',
    currentItem: undefined,
    buttonPrefix: undefined,
};
